import React from "react";
import { Typography } from "@mui/material";
import Button from "@components/Button/Button";
import { BubblePaper, BubbleWrapper, SenderLabel, Timestamp } from "@components/Chat/Chat.styles";
import type { MessageAttachment } from "@services/drybulkCargoOperationsApi";
import type { MessageApiResponse } from "@services/message-service/types";
import { useTranslation } from "react-i18next";
import AttachmentFileCard from "../components/AttachmentFileCard";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { useDownloadCargoOperationAttachment } from "../hooks/useDownloadCargoOperationAttachment";
import type { PendingAttach } from "../hooks/useMessageDraftStore";
import { getAttachmentFileName } from "../utils/attachmentFileName";
import { formatInstant } from "../utils/formatInstant";
import MessageFileUpload from "./MessageFileUpload";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail.messages`;

interface CargoMessageBubbleProps {
  message: MessageApiResponse;
  files: MessageAttachment[];
  isOwn: boolean;
  pending?: PendingAttach;
  retrying?: boolean;
  onRetry?: () => void;
  onRepick?: (files: File[]) => void;
}

const CargoMessageBubble: React.FC<CargoMessageBubbleProps> = ({
  message,
  files,
  isOwn,
  pending,
  retrying = false,
  onRetry,
  onRepick,
}) => {
  const { t } = useTranslation();
  const download = useDownloadCargoOperationAttachment();
  const testId = `drybulk-message-${message.id}`;
  const align = isOwn ? "flex-end" : "flex-start";
  const textAlign = isOwn ? "end" : "start";

  return (
    <BubbleWrapper align={align} spacing={0.5} alignItems={align} data-testid={testId}>
      <SenderLabel variant="caption" sx={{ textAlign }} data-testid={`${testId}-author`}>
        {message.authorName}
      </SenderLabel>
      <BubblePaper
        elevation={0}
        bgcolorValue={isOwn ? "#0052CC" : "#E5E7EB"}
        textcolor={isOwn ? "#FFFFFF" : "#111827"}
      >
        <Typography
          variant="body1"
          data-testid={`${testId}-body`}
          sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
        >
          {message.message}
        </Typography>
      </BubblePaper>
      {files.length > 0 && (
        <ul
          className={`flex flex-col gap-2 ${isOwn ? "self-end" : "self-start"}`}
          data-testid={`${testId}-files`}
        >
          {files.map((file) => (
            <li key={file.id}>
              <AttachmentFileCard
                fileName={getAttachmentFileName(file)}
                isDownloading={download.isBusy(file.documentUrl)}
                onView={() => void download.openAttachment(file.documentUrl)}
                onDownload={() =>
                  void download.downloadAttachment(file.documentUrl, getAttachmentFileName(file))
                }
                testId={`${testId}-file-${file.id}`}
              />
            </li>
          ))}
        </ul>
      )}
      {pending && (
        <div
          className={`flex flex-col gap-2 ${isOwn ? "self-end items-end" : "self-start items-start"}`}
          data-testid={`${testId}-pending`}
        >
          <Typography variant="caption" color="error">
            {t(`${L}.files-not-attached`)}
          </Typography>
          <ul>
            {pending.files.map((file) => (
              <li key={file.documentUrl}>
                <Typography variant="caption">{file.originalFileName ?? ""}</Typography>
              </li>
            ))}
          </ul>
          {pending.needsRepick && onRepick && (
            <>
              <Typography variant="caption">{t(`${L}.repick`)}</Typography>
              <MessageFileUpload
                testId={`${testId}-repick`}
                staged={[]}
                disabled={retrying}
                onUpload={onRepick}
              />
            </>
          )}
          {!pending.needsRepick && (
            <div>
              <Button
                variant="outlined"
                size="small"
                disabled={retrying}
                onClick={onRetry}
                testId={`${testId}-retry`}
                label={<span className="normal-case font-medium">{t(`${L}.retry`)}</span>}
              />
            </div>
          )}
        </div>
      )}
      <Timestamp
        variant="caption"
        align={isOwn ? "right" : "left"}
        sx={{ alignSelf: align }}
        data-testid={`${testId}-time`}
      >
        {formatInstant(message.creationDateTime)}
      </Timestamp>
    </BubbleWrapper>
  );
};

export default CargoMessageBubble;
